import React, { useEffect, useReducer, useContext } from 'react';
import { useIntl } from 'react-intl';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { db, auth } from '../config/firebaseConfig';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';

const API_BASE_URL =
  import.meta.env.VITE_DB_INTEGRATION_SERVICE_URL || '/db-integration';

const initialState = {
  loading: true,
  saving: false,
  syncing: false,
  isMapping: false,
  config: {
    dbType: 'postgres',
    timestamp_column: '',
    connectionUri: '',
    tableName: '',
    fieldMap: { tipo: '', bairro: '', cidade: '', preco: '', descricao: '' },
    enabled: false,
    sync_interval_hours: 6,
    last_sync: null,
  },
};

function integrationReducer(state, action) {
  switch (action.type) {
    case 'SET_CONFIG_FIELD':
      return { ...state, config: { ...state.config, [action.field]: action.payload } };
    case 'SET_FIELD_MAP':
      return {
        ...state,
        config: {
          ...state.config,
          fieldMap: { ...state.config.fieldMap, [action.field]: action.payload },
        },
      };
    case 'SET_FULL_CONFIG':
      return { ...state, config: action.payload };
    case 'SET_STATE':
      return { ...state, ...action.payload };
    case 'AUTO_MAP_SUCCESS':
      return {
        ...state,
        config: { ...state.config, fieldMap: { ...state.config.fieldMap, ...action.payload } },
        isMapping: false,
      };
    case 'ACTION_ERROR':
      return { ...state, isMapping: false, saving: false, syncing: false };
    default:
      return state;
  }
}

const Integration = () => {
  const { user } = useContext(AuthContext);
  const [state, dispatch] = useReducer(integrationReducer, initialState);
  const { loading, saving, syncing, isMapping, config } = state;
  const intl = useIntl();

  useEffect(() => {
    if (!user?.uid) return;
    let alive = true;

    const loadConfig = async () => {
      dispatch({ type: 'SET_STATE', payload: { loading: true } });
      try {
        const ref = doc(db, 'db_integrations', user.uid);
        const snap = await getDoc(ref);
        if (alive && snap.exists()) {
          const savedConfig = snap.data();
          const mergedConfig = { ...initialState.config, ...savedConfig };
          dispatch({ type: 'SET_FULL_CONFIG', payload: mergedConfig });
        }
      } catch (err) {
        console.error(err);
        toast.error(
          intl.formatMessage({
            id: 'general.errorLoading',
            defaultMessage: 'Error loading data.',
          })
        );
      } finally {
        alive && dispatch({ type: 'SET_STATE', payload: { loading: false } });
      }
    };

    loadConfig();
    return () => {
      alive = false;
    };
  }, [user, intl]);

  const getAuthHeader = async () => {
    if (!auth.currentUser) {
      toast.error(
        intl.formatMessage({
          id: 'auth.notAuthenticated',
          defaultMessage: 'You must be authenticated.',
        })
      );
      throw new Error('User not authenticated');
    }
    const idToken = await auth.currentUser.getIdToken();
    return {
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    };
  };

  const handleChange = (field, value) => {
    dispatch({ type: 'SET_CONFIG_FIELD', field, payload: value });
  };

  const handleMapChange = (field, value) => {
    dispatch({ type: 'SET_FIELD_MAP', field, payload: value });
  };

  const handleSave = async () => {
    if (!user?.uid) return;
    dispatch({ type: 'SET_STATE', payload: { saving: true } });
    try {
      await setDoc(doc(db, 'db_integrations', user.uid), config, { merge: true });
      toast.success(
        intl.formatMessage({
          id: 'integration.statusSaved',
          defaultMessage: 'Integration settings saved.',
        })
      );
    } catch (err) {
      console.error(err);
      toast.error(
        intl.formatMessage({
          id: 'saveError',
          defaultMessage: 'Could not save. Try again.',
        })
      );
    } finally {
      dispatch({ type: 'SET_STATE', payload: { saving: false } });
    }
  };

  const handleAutoMapFields = async () => {
    if (!config.connectionUri || !config.tableName) {
      toast.error(
        intl.formatMessage({
          id: 'integration.db.error.uriAndTableRequired',
          defaultMessage: 'Connection URI and Table Name are required.',
        })
      );
      return;
    }
    dispatch({ type: 'SET_STATE', payload: { isMapping: true } });
    toast.info(
      intl.formatMessage({
        id: 'integration.db.mappingInProgress',
        defaultMessage: 'Mapping columns with AI…',
      })
    );
    try {
      const authHeader = await getAuthHeader();
      const res = await axios.post(
        `${API_BASE_URL}/map-db-fields`,
        {
          db_type: config.dbType,
          connection_uri: config.connectionUri,
          table_name: config.tableName,
          user_id: user.uid,
        },
        authHeader
      );

      if (res.data?.fieldMap && Object.keys(res.data.fieldMap).length > 0) {
        dispatch({ type: 'AUTO_MAP_SUCCESS', payload: res.data.fieldMap });
        toast.success(
          intl.formatMessage({
            id: 'integration.db.mapSuccess',
            defaultMessage: 'Field mapping updated.',
          })
        );
      } else {
        toast.warn(
          intl.formatMessage({
            id: 'integration.db.mapWarning',
            defaultMessage: 'No matching columns were found.',
          })
        );
      }
    } catch (err) {
      console.error(err);
      const errorMsg =
        err.response?.data?.error ||
        intl.formatMessage({
          id: 'integration.db.mapError',
          defaultMessage: 'Error while mapping fields.',
        });
      dispatch({ type: 'ACTION_ERROR' });
      toast.error(errorMsg);
    }
  };

  const handleSyncNow = async () => {
    dispatch({ type: 'SET_STATE', payload: { syncing: true } });
    toast.info(
      intl.formatMessage({
        id: 'integration.db.syncingStart',
        defaultMessage: 'Starting sync…',
      })
    );
    try {
      const authHeader = await getAuthHeader();
      const res = await axios.post(
        `${API_BASE_URL}/run-sync`,
        { user_id: user.uid },
        authHeader
      );
      const { added = 0, updated = 0 } = res.data || {};
      toast.success(
        intl.formatMessage(
          {
            id: 'integration.db.syncSuccessMessage',
            defaultMessage: 'Sync finished. {added} added, {updated} updated.',
          },
          { added, updated }
        )
      );
      dispatch({
        type: 'SET_CONFIG_FIELD',
        field: 'last_sync',
        payload: new Date().toISOString(),
      });
    } catch (err) {
      console.error(err);
      const errorMsg =
        err.response?.data?.error ||
        intl.formatMessage({
          id: 'integration.db.syncError',
          defaultMessage: 'Error while syncing.',
        });
      dispatch({ type: 'ACTION_ERROR' });
      toast.error(errorMsg);
    } finally {
      dispatch({ type: 'SET_STATE', payload: { syncing: false } });
    }
  };

  if (loading) {
    return (
      <div className="la dashboard-content">
        <div className="loading-inline">
          {intl.formatMessage({ id: 'loading.label', defaultMessage: 'Loading…' })}
        </div>
      </div>
    );
  }

  const tableNameForPlaceholder = config.tableName || 'your_table';

  return (
    <div className="la dashboard-content">
      <h1 className="page-title">
        {intl.formatMessage({
          id: 'integration.db.title',
          defaultMessage: 'Integration with External Database',
        })}
      </h1>
      <p className="page-subtitle">
        {intl.formatMessage({
          id: 'integration.db.subtitle',
          defaultMessage:
            'Connect your PostgreSQL, MySQL or SQL Server database directly to lugarAI.',
        })}
      </p>

      {/* Status banner */}
      <div className="status-info mt-4 mb-2" role="status" aria-live="polite">
        <strong>
          {intl.formatMessage({
            id: 'integration.db.status',
            defaultMessage: 'Integration Status',
          })}
          :
        </strong>{' '}
        <span className={config.enabled ? 'text-success' : 'text-danger'}>
          {intl.formatMessage({
            id: config.enabled
              ? 'integration.db.status.active'
              : 'integration.db.status.inactive',
            defaultMessage: config.enabled ? 'Active' : 'Inactive',
          })}
        </span>
        <span className="divider">•</span>
        <strong>
          {intl.formatMessage({
            id: 'integration.db.lastSync.label',
            defaultMessage: 'Last Sync',
          })}
          :
        </strong>{' '}
        {config.last_sync
          ? new Date(config.last_sync).toLocaleString()
          : intl.formatMessage({ id: 'general.noneYet', defaultMessage: 'None yet' })}
      </div>

      {/* Connection block */}
      <div className="card">
        <div className="card-header">
          <h2 className="section-title">
            {intl.formatMessage({
              id: 'integration.db.connection.title',
              defaultMessage: 'Connection',
            })}
          </h2>
          <p className="muted">
            {intl.formatMessage({
              id: 'integration.db.connection.subtitle',
              defaultMessage: 'Configure how lugarAI connects to your database.',
            })}
          </p>
        </div>

        <div className="card-body grid-2">
          <div className="form-group">
            <label htmlFor="dbType">
              {intl.formatMessage({
                id: 'integration.db.dbType',
                defaultMessage: 'Database Type',
              })}
            </label>
            <select
              id="dbType"
              className="input-field"
              value={config.dbType}
              onChange={(e) => handleChange('dbType', e.target.value)}
            >
              <option value="postgres">
                {intl.formatMessage({
                  id: 'integration.db.dbType.postgres',
                  defaultMessage: 'PostgreSQL',
                })}
              </option>
              <option value="mysql">
                {intl.formatMessage({
                  id: 'integration.db.dbType.mysql',
                  defaultMessage: 'MySQL',
                })}
              </option>
              <option value="mssql">
                {intl.formatMessage({
                  id: 'integration.db.dbType.mssql',
                  defaultMessage: 'SQL Server',
                })}
              </option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="timestamp_column">
              {intl.formatMessage({
                id: 'integration.db.timestampColumn.label',
                defaultMessage: 'Update Timestamp Column',
              })}
            </label>
            <input
              id="timestamp_column"
              className="input-field"
              value={config.timestamp_column}
              onChange={(e) => handleChange('timestamp_column', e.target.value)}
              placeholder={intl.formatMessage({
                id: 'integration.db.timestampColumn.placeholder',
                defaultMessage: 'e.g. updated_at',
              })}
            />
            <small className="help">
              {intl.formatMessage({
                id: 'integration.db.timestampColumn.help',
                defaultMessage:
                  'Used for incremental sync. If not set, full table scans may occur.',
              })}
            </small>
          </div>

          <div className="form-group span-2">
            <label htmlFor="connectionUri">
              {intl.formatMessage({
                id: 'integration.db.connectionUri',
                defaultMessage: 'Connection URI',
              })}
            </label>
            <input
              id="connectionUri"
              className="input-field"
              value={config.connectionUri}
              onChange={(e) => handleChange('connectionUri', e.target.value)}
              placeholder={intl.formatMessage({
                id: 'integration.db.connectionUri.placeholder',
                defaultMessage:
                  'e.g. postgresql://user:password@host:port/dbname',
              })}
            />
            <small className="help">
              {intl.formatMessage({
                id: 'integration.db.connectionUri.help',
                defaultMessage:
                  'Include username, password, host, port and database name.',
              })}
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="tableName">
              {intl.formatMessage({
                id: 'integration.db.tableName',
                defaultMessage: 'Table Name',
              })}
            </label>
            <input
              id="tableName"
              className="input-field"
              value={config.tableName}
              onChange={(e) => handleChange('tableName', e.target.value)}
              placeholder={intl.formatMessage({
                id: 'integration.db.tableName.placeholder',
                defaultMessage: 'e.g. public.properties',
              })}
            />
          </div>

          <div className="form-group">
            <label htmlFor="sync_interval_hours">
              {intl.formatMessage({
                id: 'integration.db.syncIntervalHours',
                defaultMessage: 'Sync Interval (hours)',
              })}
            </label>
            <input
              id="sync_interval_hours"
              type="number"
              min="1"
              className="input-field"
              value={config.sync_interval_hours}
              onChange={(e) =>
                handleChange('sync_interval_hours', Number(e.target.value || 1))
              }
              placeholder={intl.formatMessage({
                id: 'integration.db.syncInterval.placeholder',
                defaultMessage: 'e.g. 6',
              })}
            />
            <small className="help">
              {intl.formatMessage({
                id: 'integration.db.syncInterval.help',
                defaultMessage:
                  'How often lugarAI should pull new/updated rows.',
              })}
            </small>
          </div>

          <div className="form-group switch">
            <label htmlFor="enabled">
              {intl.formatMessage({
                id: 'integration.db.enabled',
                defaultMessage: 'Enable Integration',
              })}
            </label>
            <input
              id="enabled"
              type="checkbox"
              checked={!!config.enabled}
              onChange={(e) => handleChange('enabled', e.target.checked)}
              aria-label={intl.formatMessage({
                id: 'integration.db.enabled.aria',
                defaultMessage: 'Toggle integration',
              })}
            />
          </div>
        </div>

        <div className="card-footer actions">
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={saving || syncing}
            aria-busy={saving ? 'true' : 'false'}
          >
            {saving
              ? intl.formatMessage({
                  id: 'general.saving',
                  defaultMessage: 'Saving…',
                })
              : intl.formatMessage({ id: 'general.save', defaultMessage: 'Save' })}
          </button>

          <button
            className="btn-secondary"
            onClick={handleSyncNow}
            disabled={saving || syncing || isMapping}
            aria-busy={syncing ? 'true' : 'false'}
          >
            {syncing
              ? intl.formatMessage({
                  id: 'integration.db.syncing',
                  defaultMessage: 'Syncing…',
                })
              : intl.formatMessage({
                  id: 'integration.db.syncNow',
                  defaultMessage: 'Sync Now',
                })}
          </button>
        </div>
      </div>

      {/* Mapping block */}
      <div className="card">
        <div className="card-header">
          <h2 className="section-title">
            {intl.formatMessage({
              id: 'integration.db.mappingSectionTitle',
              defaultMessage: 'Field Mapping',
            })}
          </h2>
          <p className="muted">
            {intl.formatMessage({
              id: 'integration.db.mappingSectionSubtitle',
              defaultMessage:
                'Map your table columns to LugarAI standard fields.',
            })}
          </p>
        </div>

        <div className="card-body">
          <button
            className="btn-secondary"
            onClick={handleAutoMapFields}
            disabled={isMapping || saving || syncing}
            aria-busy={isMapping ? 'true' : 'false'}
            style={{ marginBottom: '1rem' }}
          >
            {isMapping
              ? intl.formatMessage({
                  id: 'integration.db.mappingInProgress',
                  defaultMessage: 'Mapping columns with AI…',
                })
              : `🧠 ${intl.formatMessage({
                  id: 'integration.db.aiMapButton',
                  defaultMessage: 'Map with AI',
                })}`}
          </button>

          <div className="grid-2">
            {['tipo', 'bairro', 'cidade', 'preco', 'descricao'].map((field) => (
              <div key={field} className="form-group">
                <label htmlFor={`field-map-${field}`}>
                  {intl.formatMessage({
                    id: `integration.db.field.${field}`,
                    defaultMessage:
                      field === 'tipo'
                        ? 'Type'
                        : field === 'bairro'
                        ? 'Neighborhood'
                        : field === 'cidade'
                        ? 'City'
                        : field === 'preco'
                        ? 'Price'
                        : 'Description',
                  })}
                </label>
                <input
                  id={`field-map-${field}`}
                  className="input-field"
                  value={config.fieldMap[field] || ''}
                  onChange={(e) => handleMapChange(field, e.target.value)}
                  placeholder={intl.formatMessage(
                    {
                      id: 'integration.db.field.placeholder',
                      defaultMessage: 'corresponding column in {tableName}',
                    },
                    { tableName: tableNameForPlaceholder }
                  )}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="card-footer">
          <small className="muted">
            {intl.formatMessage({
              id: 'integration.db.mappingHint',
              defaultMessage:
                'Leave blank if your table does not have a corresponding column.',
            })}
          </small>
        </div>
      </div>
    </div>
  );
};

export default Integration;
